import { useMemo } from 'react';

import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { actionConfig } from 'ui/actions/action-config';
import { useFeature } from 'ui/hooks';

import { ActionGroup, ItemIcon } from './common';

export const useActionGroups = () => {
  const { t, styledTranslate } = useScopedTranslate(locale);
  const { isEnabled: hasCalendarActionFeature } = useFeature('FEATURE_CALENDAR_ACTION');
  const { isEnabled: hasCodeActionFeature } = useFeature('FEATURE_CODE_ACTION');

  return useMemo(() => {
    const sendGroup: ActionGroup = {
      type: 'send',
      actions: [
        {
          text: actionConfig.combo_send_form.label,
          icon: (
            <ItemIcon
              icon={actionConfig.combo_send_form.icon}
              variant={actionConfig.combo_send_form.variant}
            />
          ),
          type: 'combo_send_form',
        },
        {
          text: actionConfig.send_email.label,
          icon: (
            <ItemIcon
              icon={actionConfig.send_email.icon}
              variant={actionConfig.send_email.variant}
            />
          ),
          type: 'send_email',
        },
        {
          text: actionConfig.send_sms.label,
          icon: (
            <ItemIcon icon={actionConfig.send_sms.icon} variant={actionConfig.send_sms.variant} />
          ),
          type: 'send_sms',
        },
        {
          text: actionConfig.calendar_event.label,
          icon: (
            <ItemIcon
              icon={actionConfig.calendar_event.icon}
              variant={actionConfig.calendar_event.variant}
            />
          ),
          type: 'calendar_event',
          isPremium: !hasCalendarActionFeature,
        },
      ],
    };

    const documentsGroup: ActionGroup = {
      type: 'documents',
      actions: [
        {
          text: actionConfig.create_document.label,
          icon: (
            <ItemIcon
              icon={actionConfig.create_document.icon}
              variant={actionConfig.create_document.variant}
            />
          ),
          type: 'create_document',
        },
        {
          text: actionConfig.convert_document.label,
          icon: (
            <ItemIcon
              icon={actionConfig.convert_document.icon}
              variant={actionConfig.convert_document.variant}
            />
          ),
          type: 'convert_document',
        },
      ],
    };

    const utilitiesGroup: ActionGroup = {
      type: 'utilities',
      actions: [
        {
          text: (
            <Text font="semibold14">
              {styledTranslate('askAI', {
                tag: { text: t('beta'), color: 'textHelper', font: 'semibold14' },
              })}
            </Text>
          ),
          icon: <ItemIcon icon={actionConfig.ask_ai.icon} variant={actionConfig.ask_ai.variant} />,
          type: 'ask_ai',
        },
        {
          text: (
            <Text font="semibold14">
              {styledTranslate('codeAction', {
                tag: { text: t('poweredByAI'), color: 'textHelper', font: 'semibold14' },
              })}
            </Text>
          ),
          icon: <ItemIcon icon={actionConfig.code.icon} variant={actionConfig.code.variant} />,
          type: 'code',
          isPremium: !hasCodeActionFeature,
        },
        {
          text: actionConfig.formatter.label,
          icon: (
            <ItemIcon icon={actionConfig.formatter.icon} variant={actionConfig.formatter.variant} />
          ),
          type: 'formatter',
        },
        {
          text: actionConfig.extract_text.label,
          icon: (
            <ItemIcon
              icon={actionConfig.extract_text.icon}
              variant={actionConfig.extract_text.variant}
            />
          ),
          type: 'extract_text',
        },
        {
          text: actionConfig.anonymize_text.label,
          icon: (
            <ItemIcon
              icon={actionConfig.anonymize_text.icon}
              variant={actionConfig.anonymize_text.variant}
            />
          ),
          type: 'anonymize_text',
        },
        {
          text: actionConfig.placeholder.label,
          icon: (
            <ItemIcon
              icon={actionConfig.placeholder.icon}
              variant={actionConfig.placeholder.variant}
            />
          ),
          type: 'placeholder',
        },
      ],
    };

    const flowControlGroup: ActionGroup = {
      type: 'flowControl',
      actions: [
        {
          text: actionConfig.journey_branch.label,
          icon: (
            <ItemIcon
              icon={actionConfig.journey_branch.icon}
              variant={actionConfig.journey_branch.variant}
            />
          ),
          type: 'journey_branch',
        },
        {
          text: actionConfig.condition_v2.label,
          icon: (
            <ItemIcon
              icon={actionConfig.condition_v2.icon}
              variant={actionConfig.condition_v2.variant}
            />
          ),
          type: 'condition_v2',
        },
        {
          text: actionConfig.add_to_journey.label,
          icon: (
            <ItemIcon
              icon={actionConfig.add_to_journey.icon}
              variant={actionConfig.add_to_journey.variant}
            />
          ),
          type: 'add_to_journey',
        },
        {
          text: actionConfig.journey_loop.label,
          icon: (
            <ItemIcon
              icon={actionConfig.journey_loop.icon}
              variant={actionConfig.journey_loop.variant}
            />
          ),
          type: 'journey_loop',
          values: {
            days: { value: '0' },
            hours: { value: '12' },
            minutes: { value: '0' },
          },
        },
      ],
    };

    const waitGroup: ActionGroup = {
      type: 'wait',
      actions: [
        {
          text: actionConfig.wait_until_relative_date.label,
          icon: (
            <ItemIcon
              icon={actionConfig.wait_until_relative_date.icon}
              variant={actionConfig.wait_until_relative_date.variant}
            />
          ),
          type: 'wait_until_relative_date',
        },
        {
          text: actionConfig.wait_for_time.label,
          icon: (
            <ItemIcon
              icon={actionConfig.wait_for_time.icon}
              variant={actionConfig.wait_for_time.variant}
            />
          ),
          type: 'wait_for_time',
        },
        {
          text: actionConfig.wait_for_attribute_change.label,
          icon: (
            <ItemIcon
              icon={actionConfig.wait_for_attribute_change.icon}
              variant={actionConfig.wait_for_attribute_change.variant}
            />
          ),
          type: 'wait_for_attribute_change',
        },
        {
          text: actionConfig.journey_merge.label,
          icon: (
            <ItemIcon
              icon={actionConfig.journey_merge.icon}
              variant={actionConfig.journey_merge.variant}
            />
          ),
          type: 'journey_merge',
        },
      ],
    };

    const updateGroup: ActionGroup = {
      type: 'update',
      actions: [
        {
          text: actionConfig.update_traveller.label,
          icon: (
            <ItemIcon
              icon={actionConfig.update_traveller.icon}
              variant={actionConfig.update_traveller.variant}
            />
          ),
          type: 'update_traveller',
        },
        {
          text: actionConfig.update_fields.label,
          icon: (
            <ItemIcon
              icon={actionConfig.update_fields.icon}
              variant={actionConfig.update_fields.variant}
            />
          ),
          type: 'update_fields',
        },
      ],
    };

    const leftColumnGroups = [sendGroup, documentsGroup, utilitiesGroup];
    const rightColumnGroups = [flowControlGroup, waitGroup, updateGroup];

    return { leftColumnGroups, rightColumnGroups };
  }, [hasCalendarActionFeature, hasCodeActionFeature, t, styledTranslate]);
};

const locale = {
  en: {
    askAI: 'Ask AI {tag}',
    beta: '(Beta)',
    codeAction: 'Code action {tag}',
    poweredByAI: '(Powered by AI)',
  },
};
